<template>
  <div class="connect-container">
    <FeedHeader
      @premium-status="handlePremiumStatus"
    />

    <div class="connect-scrollable" ref="scrollContainer">

      <div class="connect-layout">

      <div class="connect-filters">
        <div ref="filtersInner" class="filters-inner">
          <ConnectFilters
            :initial-consultant="selectedConsultant"
            :initial-remote="selectedRemote"
            :initial-date="selectedDate"
            :is-premium-enabled="isPremiumEnabled"
            @filtersChanged="onFiltersChanged"
            @premiumClick="openPremiumPaywall"
          />
        </div>
      </div>

        <div class="connect-main">

          <div class="search-wrapper">

            <div
              v-if="isLoading"
              class="search-icon-skeleton"
            ></div>

            <button
              v-else-if="!isSearchOpen"
              type="button"
              class="search-trigger"
              aria-label="Открыть поиск"
              @click="toggleSearch"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="10.8"
                  cy="10.8"
                  r="5.8"
                  stroke="currentColor"
                  stroke-width="1.7"
                />

                <path
                  d="M15.2 15.2L19.2 19.2"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                />
              </svg>
            </button>

            <transition name="search-expand">
              <div
                v-if="isSearchOpen"
                class="search-panel"
              >
                <div class="search-leading-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      cx="10.8"
                      cy="10.8"
                      r="5.8"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />

                    <path
                      d="M15.2 15.2L19.2 19.2"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                    />
                  </svg>
                </div>

                <input
                  v-model="searchText"
                  type="text"
                  class="search-input"
                  placeholder="Найти пользователя"
                  @keyup.enter="performSearch"
                />

                <button
                  v-if="searchText"
                  type="button"
                  class="search-clear"
                  aria-label="Очистить"
                  @click="searchText = ''"
                >
                  <svg
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M6 6L14 14"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />

                    <path
                      d="M14 6L6 14"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>

                <button
                  type="button"
                  class="search-submit"
                  aria-label="Найти"
                  @click="performSearch"
                >
                  <svg
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M4 10H15"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />

                    <path
                      d="M11 6L15 10L11 14"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>

                <button
                  type="button"
                  class="search-close"
                  aria-label="Закрыть поиск"
                  @click="toggleSearch"
                >
                  <svg
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M6 6L14 14"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />

                    <path
                      d="M14 6L6 14"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>
              </div>
            </transition>

            </div>

        <template v-if="isLoading">
          <div class="connect-grid">
            <ConnectCellSkeleton v-for="n in 12" :key="'skeleton-' + n" />
          </div>
        </template>

        <template v-else-if="isSearching">
          <div class="search-loader">
            <div class="blue-spinner"></div>
          </div>
        </template>

        <template
          v-else-if="
            !isSearching &&
            users.length === 0
          "
        >
          <div class="empty-state">
            <img
              src="../assets/empty-vacancies.png"
              alt="Ничего не найдено"
              class="empty-icon"
            />

            <p class="empty-message">
              К сожалению, по заданным условиям<br />
              ничего не найдено
            </p>
          </div>
        </template>

          <template v-else>
            <div class="connect-grid">
              <ConnectCell
                v-for="user in users"
                :key="user.user_id"
                :user-id="user.user_id"
                :name="user.name"
                :avatar="user.image_url"
                :age="user.age"
                :specifics="user.specifics"
                :is-consultant="user.is_consultant"
                :is-remote="user.is_remote"
                :is-requested="user.is_requested"
                :company-name="user.company_name"
                @askQuestion="openConsultantDialog"
              />
              <template v-if="isFetchingMore">
              <ConnectCellSkeleton
                v-for="n in 4"
                :key="'pagination-skeleton-' + n"
              />
            </template>
            </div>
          </template>
        </div>
      </div>
    </div>

    <Footer v-if="!isMobile" class="feed-footer" />
    <MobileTabBar v-else />
  </div>
    <AskConsultantDrawer
    :isOpen="isDrawerOpen"
    :consultantName="user?.name"
    :consultantId="user?.user_id"
    @close="isDrawerOpen = false"
    @submitted="handleConsultantQuestion"
  />
  <RequestSnackbar v-if="showRequestSnackbar" />

  <PaywallOverlay
    v-if="showPaywall"
    @close="closePremiumPaywall"
  />

</template>

  <script setup>
  
  import { ref, onMounted, onUnmounted } from "vue";
  
  import PaywallOverlay from "../views/Paywall.vue";
  import MobileTabBar from "../components/MobileTabBar2.vue";
  import FeedHeader from "../components/FeedHeader.vue";
  import ConnectCell from "../components/ConnectCell.vue";
  import ConnectCellSkeleton from "../components/ConnectCellSkeleton.vue";
  import ConnectService from "../api/ConnectService.js";
  import Footer from "../components/MiniFooter.vue";
  import ConnectFilters from "../components/ConnectFilters.vue";
  import RequestSnackbar from "../components/RequestSnackbar.vue";
  import AskConsultantDrawer from "../views/AskConsultantDrawer.vue";

  import { useRouter, useRoute } from "vue-router";

  const route = useRoute();
  const router = useRouter();
  const isMobile = ref(window.innerWidth <= 768);
  const selectedConsultant = ref("all");
  const selectedRemote = ref("all");
  const selectedDate = ref("all");

  const showRequestSnackbar = ref(false);
  const isDrawerOpen = ref(false);

  const isPremiumEnabled = ref(false);

  const showPaywall = ref(false);

  function openPremiumPaywall() {
    showPaywall.value = true;
  }

  function closePremiumPaywall() {
    showPaywall.value = false;
  }


  function handlePremiumStatus(
    value
  ) {
    isPremiumEnabled.value =
      value === true;

    console.log(
      "💎 Connect получил premium:",
      isPremiumEnabled.value
    );
  }

  const onFiltersChanged = (filters) => {

    selectedConsultant.value =
      filters.consultant || "all";

    selectedRemote.value =
      filters.remote || "all";

    selectedDate.value =
      filters.date || "all";


    updateFiltersInUrl(filters);

    resetAndFetch();
  };

  function loadFiltersFromUrl() {

  selectedConsultant.value =
    route.query.consultants === "true"
      ? "consultants-only"
      : "all";


  selectedRemote.value =
    route.query.remote === "true"
      ? "remote-only"
      : "all";


  selectedDate.value =
    route.query.date ||
    "all";
  }

  const checkIsMobile = () => {
    isMobile.value = window.innerWidth <= 768;
  };

  const filtersInner = ref(null);
  const isSearching = ref(false);
  const updateFiltersMargin = () => {
    const scrollTop = scrollContainer.value?.scrollTop || 0;
    if (filtersInner.value) {
      filtersInner.value.style.marginTop = scrollTop > 30 ? '-70px' : '0px';
    }
  };

  const scrollContainer = ref(null);
  const users = ref([]);
  const currentPage = ref(1);
  const isLoading = ref(true);
  const isFetchingMore = ref(false);
  const searchText = ref("");
  const isInitializing = ref(true);

  const performSearch = async () => {
  if (!searchText.value.trim()) {
    ConnectService.resetPagination();

    users.value = await ConnectService.fetchWorkers(20, buildFilters());
    return;
  }

  isSearching.value = true;

  const rawResults = await ConnectService.searchUsers(searchText.value.trim());

  users.value = rawResults.map(u => ({
    user_id: u.user_id,
    name: u.name,
    age: u.age,
    specifics: u.specifics,
    image_url: u.image_url,
  }));

  isSearching.value = false;
};
  
async function loadNextPage() {
  if (
    isFetchingMore.value ||
    isLoading.value ||
    searchText.value.trim()
  ) {
    return;
  }

  isFetchingMore.value = true;

  try {
    currentPage.value++;

    const moreUsers =
      await ConnectService.fetchWorkers(
        20,
        buildFilters()
      );


    const existingIds = new Set(
      users.value.map(
        user => user.user_id
      )
    );


    const uniqueUsers =
      moreUsers.filter(
        user =>
          !existingIds.has(
            user.user_id
          )
      );


    users.value = [
      ...users.value,
      ...uniqueUsers
    ];


  } catch (error) {
    console.error(
      "❌ Ошибка загрузки следующей страницы:",
      error
    );

  } finally {
    isFetchingMore.value = false;
  }
}
  
  function handleScroll() {
    const el = scrollContainer.value;
    if (!el) return;
  
    const scrollTop = el.scrollTop;
    const containerHeight = el.clientHeight;
    const scrollHeight = el.scrollHeight;
  
    if (scrollTop + containerHeight >= scrollHeight - 100) {
      loadNextPage();
    }
  }

  async function resetAndFetch() {
  isLoading.value = true;

  ConnectService.resetPagination();
  currentPage.value = 1;

  users.value = await ConnectService.fetchWorkers(
    20,
    buildFilters()
  );

  isLoading.value = false;
}
  
  const isSearchOpen = ref(false);

  async function toggleSearch() {
  const wasOpen = isSearchOpen.value;

  // сохраняем текст ДО очистки
  const hadSearchText = searchText.value.trim().length > 0;

  isSearchOpen.value = !isSearchOpen.value;

  if (wasOpen) {

    // если поиск не использовался — просто закрываем
    if (!hadSearchText) {
      searchText.value = "";
      return;
    }

    searchText.value = "";

    isLoading.value = true;
    ConnectService.resetPagination();
    currentPage.value = 1;

    users.value = await ConnectService.fetchWorkers(20, buildFilters());
    isLoading.value = false;
  }
}

function buildFilters() {
  return {
    isRemote: selectedRemote.value === "remote-only",
    isConsultant: selectedConsultant.value === "consultants-only",
    created: selectedDate.value !== "all" ? selectedDate.value : null
  };
}

function updateFiltersInUrl(filters) {

  const query = {};

  if (filters.consultant === "consultants-only") {
    query.consultants = "true";
  }

  if (filters.remote === "remote-only") {
    query.remote = "true";
  }

  if (filters.date !== "all") {
    query.date = filters.date;
  }


  router.replace({
    query
  });
}
  
onMounted(async () => {

  loadFiltersFromUrl();

  window.addEventListener("resize", checkIsMobile); 
  checkIsMobile();

  users.value = [];
  ConnectService.resetPagination();
  currentPage.value = 1;
  isLoading.value = true;
  users.value = [];

  if (scrollContainer.value) {
    scrollContainer.value.addEventListener("scroll", handleScroll);
    scrollContainer.value.addEventListener("scroll", updateFiltersMargin);
  }
  
  users.value =
  await ConnectService.fetchWorkers(
    20,
    buildFilters()
  );

  isLoading.value = false;

  isInitializing.value = false;

  await loadNextPage();
});
  
onUnmounted(() => {
  
  window.removeEventListener("resize", checkIsMobile);

  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener("scroll", handleScroll);
    scrollContainer.value.removeEventListener("scroll", updateFiltersMargin);
  }
});

const openConsultantDialog = (userId) => {
isDrawerOpen.value = true;
  setTimeout(() => {
    showRequestSnackbar.value = false;
  }, 3000);
};

</script>
  
<style scoped>

.connect-container {
  width: 100%;
  height: 100dvh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background-color: var(--background-color);
  overflow: hidden;
}

.connect-scrollable {
  flex: 1;
  overflow-y: auto;
  padding-top: 20px;
  padding-bottom: 20px;
  scrollbar-gutter: stable;
}

.connect-layout {
  display: flex;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0;
  gap: 0px;
}

.connect-filters {
  width: 280px;
  flex-shrink: 0;
  position: sticky;
  top: 60px;
  align-self: flex-start;
  height: fit-content;
  margin-top: 20px;
}

.connect-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 870px;
}

.connect-grid {
  display: grid;

  grid-template-columns:
    repeat(
      auto-fill,
      minmax(157px, 1fr)
    );

  gap: 12px;
  column-gap: 40px;

  max-width: 800px;

  margin-left: 10px;
}

.connect-grid::-webkit-scrollbar {
  display: none;
}

.bottom-loader {
  width: 100%; 
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
  grid-column: 1 / -1;
}

.spinner-icon {
  width: 28px;
  height: 28px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.feed-footer {
  flex-shrink: 0;
  width: 100%;
}

.filters-inner {
  margin-top: 0px;
  transition: margin-top 0.3s ease;
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  margin-bottom: 20px;
  width: 840px;
}

.search-icon-button {
  background: transparent;
  border: none;
  outline: none;
  appearance: none;
  -webkit-appearance: none;

  cursor: pointer;
  width: 40px;
  height: 40px;

  display: flex;
  align-items: center;
  justify-content: center;
}

.search-bar-expanded .search-icon-button {
  margin-right: 0 !important;
}

.search-icon-button:focus {
  outline: none;
  box-shadow: none;
}

.search-icon-button img {
  width: 22px;
  height: 22px;
  filter: none;
}

.search-icon-button {
  color: #2173E5;
}

.search-icon-button img {
  filter: brightness(0) saturate(100%) invert(29%) sepia(92%)
          saturate(1962%) hue-rotate(204deg)
          brightness(96%) contrast(94%);
}

.search-bar-expanded {
  position: absolute;
  right: 0;
  width: 800px;
  margin-right: 10px;
  display: flex;
  align-items: center;
  padding: 10px 16px;
  gap: 10px;
  position: absolute;
}

.search-bar-expanded {
  position: absolute;
  overflow: hidden;
  padding-right: 0px;
}

.search-bar-expanded::before {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 1px;
  background: rgba(120,120,120,0.3);
}

.search-bar-expanded::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0;
  width: 0%;
  height: 2px;
  background: linear-gradient(90deg, #2173E5, #4f9dff);
  transition: width 0.35s ease;
}

.search-bar-expanded button {
  border: none;
  border-radius: 50%;
  padding: 8px;
  cursor: pointer;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-bar-expanded:focus-within::after {
  width: 100%;
}

html[data-theme='dark'] .search-bar-expanded::before {
  background: rgba(255,255,255,0.2);
}

html[data-theme='dark'] .search-bar-input {
  background: transparent;
  color: #fff;
  caret-color: #fff;

}

.search-bar-expanded button {
  background: transparent;
  border: none;
  padding: 6px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fade-width-enter-active,
.fade-width-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.fade-width-enter-from {
  opacity: 0;
  transform: translateX(30px);
}
.fade-width-enter-to {
  opacity: 1;
  transform: translateX(0);
}

.fade-width-leave-from {
  opacity: 1;
  transform: translateX(0);
}
.fade-width-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.search-icon-button.hidden {
  opacity: 0;
  pointer-events: none;
}

.search-loader {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 700px;
}

.blue-spinner {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid rgba(33, 115, 229, 0.2);
  border-top-color: #2173E5;
  animation: spin 0.8s linear infinite;
}

html {
  scrollbar-gutter: stable;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 80px;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  margin-bottom: 12px;
}

.empty-message {
  font-size: 14px;
  font-family: "Eastman-Regular", sans-serif;
  color: #4e4e4e;
  line-height: 1.5;
}

html[data-theme="dark"] .empty-message {
  color: #bdbdbd;
}

@media (max-width: 430px) {

/* ===== FILTERS ===== */

.connect-filters {
  display: none;
}

/* ===== LAYOUT ===== */

.connect-layout {
  display: block;
  width: 100%;
}

.connect-main {
  width: 100%;
  max-width: 100%;
  padding: 0 10px;
  box-sizing: border-box;
}


/* ===== GRID ===== */

.connect-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
  margin: 0;
  padding: 0 4px;
  box-sizing: border-box;
}

/* ===== CARD FIX ===== */

.connect-grid > * {
width: 100%;
max-width: 100%;
}

/* ===== EMPTY ===== */

.empty-state {
  margin-top: 60px;
  padding: 0 20px;
}

/* ===== SEARCH LOADER ===== */

.search-loader {
  min-height: 400px;
}

/* ===== SCROLL ===== */

.connect-scrollable {
  flex: 1;
  min-height: 0;

  padding-top: 10px;
  padding-bottom: 16px;

  overflow-y: auto;
}
/* ===== FOOTER ===== */

.feed-footer {
  position: relative;
}

/* ===== SEARCH ICON ===== */

.search-icon-button {
  width: 36px;
  height: 36px;
}

.search-icon-button img {
  width: 20px;
  height: 20px;
}

.connect-cell {
  position: relative;

  width: 180px;
  height: 220px;

  padding:
    16px
    8px;

  display: flex;
  flex-direction: column;
  align-items: center;

  overflow: hidden;

  text-align: center;

  cursor: pointer;
  user-select: none;

  color: #f0f1f3;

  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border:
    1px solid
    rgba(255, 255, 255, 0.065);

  border-radius: 18px;

  box-shadow:
    0
    12px
    28px
    rgba(0, 0, 0, 0.16);
}

}

.search-icon-skeleton {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e4e4e4;
  position: relative;
  overflow: hidden;
}

.search-icon-skeleton::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255,255,255,0.55),
    transparent
  );
  animation: searchIconShimmer 1.2s infinite;
}

html[data-theme="dark"] .search-icon-skeleton {
  background: #333;
}

html[data-theme="dark"] .search-icon-skeleton::after {
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255,255,255,0.12),
    transparent
  );
}

@keyframes searchIconShimmer {
  100% {
    transform: translateX(100%);
  }
}

@media (max-width: 430px) {
  .search-icon-skeleton {
    width: 36px;
    height: 36px;
  }
}

/* ============================================================
   SEARCH
============================================================ */

.search-wrapper {
  position: relative;

  width: 840px;
  height: 44px;

  display: flex;
  align-items: center;
  justify-content: flex-end;

  margin-bottom: 20px;
}


/* ============================================================
   CLOSED BUTTON
============================================================ */

.search-trigger {
  width: 40px;
  height: 40px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  color: #58789f;

  background: #ffffff;

  border:
    1px solid
    rgba(35, 55, 80, 0.07);

  border-radius: 12px;

  box-shadow:
    0 7px 18px
    rgba(15, 23, 42, 0.045);

  cursor: pointer;

  transition:
    color .18s ease,
    background-color .18s ease,
    border-color .18s ease,
    transform .18s ease,
    box-shadow .18s ease;
}


.search-trigger svg {
  width: 18px;
  height: 18px;
}


.search-trigger:hover {
  color: #347fe7;

  background: #f7f9fc;

  border-color:
    rgba(55, 127, 228, 0.12);

  transform:
    translateY(-1px);

  box-shadow:
    0 10px 22px
    rgba(15, 23, 42, 0.07);
}


/* ============================================================
   PANEL
============================================================ */

.search-panel {
  position: absolute;

  right: 0;

  width: 520px;
  height: 44px;

  display: flex;
  align-items: center;

  box-sizing: border-box;

  padding:
    0
    6px
    0
    13px;

  background:
    rgba(255, 255, 255, 0.96);

  border:
    1px solid
    rgba(35, 55, 80, 0.075);

  border-radius: 14px;

  box-shadow:
    0 10px 28px
    rgba(15, 23, 42, 0.07);

  backdrop-filter:
    blur(14px);

  -webkit-backdrop-filter:
    blur(14px);

  overflow: hidden;
}


/* ============================================================
   LEADING ICON
============================================================ */

.search-leading-icon {
  width: 20px;
  height: 20px;

  flex:
    0
    0
    20px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #8d99a8;
}


.search-leading-icon svg {
  width: 17px;
  height: 17px;
}


/* ============================================================
   INPUT
============================================================ */

.search-input {
  min-width: 0;

  flex: 1;

  height: 100%;

  box-sizing: border-box;

  padding:
    0
    10px;

  color: #3c434c;

  background: transparent;

  border: none;
  outline: none;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 12px;

  line-height: 1;
}


.search-input::placeholder {
  color: #a1a7af;
}


/* ============================================================
   ACTIONS
============================================================ */

.search-clear,
.search-submit,
.search-close {
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  border: none;

  cursor: pointer;

  transition:
    color .18s ease,
    background-color .18s ease,
    transform .18s ease;
}


.search-clear {
  width: 28px;
  height: 28px;

  color: #9aa1aa;

  background: transparent;

  border-radius: 8px;
}


.search-clear:hover {
  color: #5e6670;

  background:
    rgba(35, 50, 70, 0.05);
}


.search-clear svg {
  width: 13px;
  height: 13px;
}


/* SEARCH */

.search-submit {
  width: 32px;
  height: 32px;

  margin-left: 3px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3d86e5,
      #3471c2
    );

  border-radius: 9px;

  box-shadow:
    0 6px 14px
    rgba(50, 115, 205, 0.16);
}


.search-submit:hover {
  transform:
    translateY(-1px);

  box-shadow:
    0 8px 17px
    rgba(50, 115, 205, 0.22);
}


.search-submit svg {
  width: 14px;
  height: 14px;
}


/* CLOSE */

.search-close {
  width: 30px;
  height: 30px;

  margin-left: 4px;

  color: #8e969f;

  background: transparent;

  border-radius: 8px;
}


.search-close:hover {
  color: #515860;

  background:
    rgba(35, 50, 70, 0.05);
}


.search-close svg {
  width: 13px;
  height: 13px;
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.search-trigger {
  color: #96a8be;

  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(255, 255, 255, 0.065);

  box-shadow:
    0
    9px
    22px
    rgba(0, 0, 0, 0.15);
}


html[data-theme="dark"]
.search-trigger:hover {
  color: #a9c8f3;

  background:
    #25282e;

  border-color:
    rgba(120, 160, 215, 0.10);
}


html[data-theme="dark"]
.search-panel {
  background:
    linear-gradient(
      145deg,
      rgba(34, 36, 42, 0.98),
      rgba(29, 31, 36, 0.98)
    );

  border-color:
    rgba(255, 255, 255, 0.065);

  box-shadow:
    0
    14px
    32px
    rgba(0, 0, 0, 0.22);
}


html[data-theme="dark"]
.search-leading-icon {
  color: #758396;
}


html[data-theme="dark"]
.search-input {
  color: #e1e4e8;

  caret-color: #8fb9ef;
}


html[data-theme="dark"]
.search-input::placeholder {
  color: #646a72;
}


html[data-theme="dark"]
.search-clear {
  color: #757c85;
}


html[data-theme="dark"]
.search-clear:hover,
html[data-theme="dark"]
.search-close:hover {
  color: #c3c8cf;

  background:
    rgba(255, 255, 255, 0.045);
}


html[data-theme="dark"]
.search-close {
  color: #858c95;
}


html[data-theme="dark"]
.search-submit {
  background:
    linear-gradient(
      135deg,
      #3d77bc,
      #315c91
    );

  box-shadow:
    0
    7px
    16px
    rgba(0, 0, 0, 0.18);
}


/* ============================================================
   ANIMATION
============================================================ */

.search-expand-enter-active,
.search-expand-leave-active {
  transition:
    opacity .20s ease,
    transform .24s cubic-bezier(.2, .8, .3, 1);
}


.search-expand-enter-from,
.search-expand-leave-to {
  opacity: 0;

  transform:
    translateX(18px)
    scaleX(.96);

  transform-origin:
    right center;
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 430px) {

  .search-wrapper {
    width: 100%;
    height: 42px;

    margin-bottom: 18px;
  }


  .search-trigger {
    width: 38px;
    height: 38px;
  }


  .search-panel {
    width: 100%;
    height: 42px;

    padding-left: 11px;

    border-radius: 13px;
  }


  .search-input {
    font-size: 11px;
  }


  .search-submit {
    width: 30px;
    height: 30px;
  }

}

</style>